import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTour } from './ui/TourManager';
import { dashboardTourSteps } from './ui/tourSteps';
import {
  Activity, Calendar as CalendarIcon, TrendingUp, TrendingDown,
  Layers, CheckCircle2, BarChart3, Users, Mail, Settings, MousePointerClick, Zap, AlertTriangle, PlayCircle, Reply, ShieldAlert, ChevronRight
} from 'lucide-react';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip as ReTooltip, PieChart, Pie, Cell, Legend
} from 'recharts';
import { getDashboardSummary } from '../api/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Skeleton } from './ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Calendar } from './ui/calendar';
import dayjs from 'dayjs';
import RevenueDashboard from './RevenueDashboard';
import UnifiedInsightsDashboard from './UnifiedInsightsDashboard';
import OnboardingModal from './OnboardingModal';
import { formatWithPattern } from '../lib/i18n';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, sent: 0, notSended: 0, processing: 0 });
  const [crmFunnel, setCrmFunnel] = useState(null);
  const [barData, setBarData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [recentEmails, setRecentEmails] = useState([]);
  const [sequenceData, setSequenceData] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [actionHub, setActionHub] = useState(null);
  const [loading, setLoading] = useState(true);
	const [showOnboarding, setShowOnboarding] = useState(false);
	const { startTour } = useTour();
  const v = "v";
	useEffect(() => {
		const onboardingComplete = localStorage.getItem('onboardingComplete');
		if (onboardingComplete !== 'true') {
			setShowOnboarding(true);
		}
	}, []);

	useEffect(() => {
		// Auto-start tour on first visit
		const tourShown = localStorage.getItem('dashboardTourShown');
		if (tourShown !== 'true') {
			startTour('dashboard', dashboardTourSteps);
		}
	}, [startTour]);

  // Date filter state
  const [dateRange, setDateRange] = useState({
    from: dayjs().subtract(7, 'day').toDate(),
    to: dayjs().toDate()
  });

  const fetchStats = React.useCallback(async () => {
    try {
      if (!dateRange.from || !dateRange.to) return;
      const res = await getDashboardSummary({
        startDate: dayjs(dateRange.from).format('YYYY-MM-DD'),
        endDate: dayjs(dateRange.to).format('YYYY-MM-DD')
      });

      const data = res.data || {};
      const volumeStats = data.volumeStats || [];

      setStats(data.overviewStats || { total: 0, sent: 0, notSended: 0, processing: 0 });
      setActionHub(data.actionHub || null);
      setBarData(volumeStats.map((item) => ({
        name: item.date,
        total: (item.sent || 0) + (item.failed || 0)
      })));
      setRecentEmails(data.recentActivity || []);
      setCountryData(data.geoDistribution || []);
      setCrmFunnel(data.crmFunnel || null);
      setSequenceData(data.sequenceSummary || null);
      setCampaignData(data.campaignSummary || null);
      setLoading(false);
    } catch (error) {
      console.error('Critical Dashboard data fetch error:', error);
      setLoading(false);
    }
  }, [dateRange]);

  dayjs.locale(i18n.language?.startsWith('tr') ? 'tr' : 'en');

  const actionCards = [
    {
      key: 'needsAttention',
      title: t('dashboard.actionCards.needsAttention.title'),
      icon: AlertTriangle,
      color: 'text-rose-600',
      accent: 'border-rose-500/20 bg-rose-500/5',
      cta: t('dashboard.actionCards.needsAttention.cta')
    },
    {
      key: 'readyToLaunch',
      title: t('dashboard.actionCards.readyToLaunch.title'),
      icon: PlayCircle,
      color: 'text-emerald-600',
      accent: 'border-emerald-500/20 bg-emerald-500/5',
      cta: t('dashboard.actionCards.readyToLaunch.cta')
    },
    {
      key: 'replyPressure',
      title: t('dashboard.actionCards.replyPressure.title'),
      icon: Reply,
      color: 'text-amber-600',
      accent: 'border-amber-500/20 bg-amber-500/5',
      cta: t('dashboard.actionCards.replyPressure.cta')
    },
    {
      key: 'senderHealth',
      title: t('dashboard.actionCards.senderHealth.title'),
      icon: ShieldAlert,
      color: 'text-sky-600',
      accent: 'border-sky-500/20 bg-sky-500/5',
      cta: t('dashboard.actionCards.senderHealth.cta')
    }
  ];

  const highlightedSequences = sequenceData?.highlightedSequences || [];
  const highlightedCampaigns = campaignData?.highlightedCampaigns || [];

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      fetchStats();
    }
    const interval = setInterval(fetchStats, 60000); // Poll every minute instead of 30s to reduce load
    return () => clearInterval(interval);
  }, [dateRange, fetchStats]);

  return (
    <>
	    {showOnboarding && (
		<OnboardingModal onComplete={() => setShowOnboarding(false)} />
	    )}
	    <div className="flex-1 space-y-6 p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-2">
            <Activity className="w-8 h-8 text-primary" /> {t('dashboard.title')}
          </h2>
          <p className="text-muted-foreground mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex items-center space-x-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[260px] justify-start text-left font-medium border-border shadow-sm">
                <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                {dateRange.from && dateRange.to ? (
                  <>
                    {formatWithPattern(dateRange.from, 'd MMM yyyy', i18n.language)} - {formatWithPattern(dateRange.to, 'd MMM yyyy', i18n.language)}
                  </>
                ) : (
                  <span>{t('dashboard.selectDate')}</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange.from}
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-primary/30 text-primary hover:bg-primary/5"
            onClick={() => startTour('dashboard', dashboardTourSteps)}
          >
            <MousePointerClick className="w-4 h-4 mr-2" />
            Turu Başlat
          </Button>
        </div>

      {/* Action Hub */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 dashboard-stats">
        {actionCards.map((card) => {
          const data = actionHub?.[card.key];
          const Icon = card.icon;

          return (
            <Card key={card.key} className={`shadow-sm border ${card.accent}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">{card.title}</p>
                    <div className="mt-3 text-3xl font-black text-foreground">{data?.count ?? 0}</div>
                  </div>
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-background/80 ${card.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
                <p className="mt-3 text-sm text-muted-foreground min-h-[44px]">
                  {data?.summary || t('dashboard.actionHubFallback')}
                </p>
                <button
                  type="button"
                  onClick={() => navigate(
                    data?.route || '/dashboard',
                    card.key === 'readyToLaunch'
                      ? { state: { dashboardStatusFilter: 'DRAFT' } }
                      : card.key === 'replyPressure'
                        ? { state: { dashboardFilters: { isArchived: 'false', isRead: 'false', classification: 'REPLY' } } }
                        : {}
                  )}
                  className={`mt-4 inline-flex items-center text-xs font-black uppercase tracking-widest ${card.color} hover:underline`}
                >
                  {card.cta} <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Shortcuts Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 quick-actions">
        <Button 
          variant="outline" 
          className="h-20 flex flex-col items-center justify-center gap-2 border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors shadow-sm"
          onClick={() => navigate('/campaigns')}
        >
          <Zap className="w-5 h-5 text-primary" />
          <span className="font-semibold text-primary">{t('dashboard.shortcuts.newCampaign')}</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-20 flex flex-col items-center justify-center gap-2 border-border shadow-sm hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-colors"
          onClick={() => navigate('/crm/contacts')}
        >
          <Users className="w-5 h-5 text-emerald-500" />
          <span className="font-semibold text-foreground">{t('dashboard.shortcuts.addContacts')}</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-20 flex flex-col items-center justify-center gap-2 border-border shadow-sm hover:border-blue-500/30 hover:bg-blue-500/5 transition-colors"
          onClick={() => navigate('/inbox')}
        >
          <Mail className="w-5 h-5 text-blue-500" />
          <span className="font-semibold text-foreground">{t('dashboard.shortcuts.viewInbox')}</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-20 flex flex-col items-center justify-center gap-2 border-border shadow-sm hover:border-gray-500/30 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          onClick={() => navigate('/settings')}
        >
          <Settings className="w-5 h-5 text-muted-foreground" />
          <span className="font-semibold text-foreground">{t('dashboard.shortcuts.settings')}</span>
        </Button>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-muted p-1 rounded-lg">
          <TabsTrigger value="overview" className="rounded-md font-medium">{t('dashboard.overview')}</TabsTrigger>
          <TabsTrigger value="executive" className="rounded-md font-medium">{t('dashboard.executiveInsights')}</TabsTrigger>
          <TabsTrigger value="revenue" className="rounded-md font-medium">{t('dashboard.pipelineRevenue')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 animate-in fade-in-50 duration-500">
          
          {/* High-Level Stats Cards */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card className="shadow-sm border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('dashboard.totalEmails')}</CardTitle>
                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500">
                  <Mail className="h-4 w-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-black text-foreground">{stats.total}</div>
                <div className="flex items-center text-xs text-muted-foreground mt-2 font-medium">
                  {t('dashboard.totalEmailsDesc', { defaultValue: 'Currently queued or processing' })}
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-sm border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('dashboard.sent')}</CardTitle>
                <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                  <TrendingUp className="h-4 w-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-black text-foreground">{stats.sent}</div>
                <div className="flex items-center text-xs text-muted-foreground mt-2 font-medium">
                  {t('dashboard.sentDesc', { defaultValue: 'Successful deliveries in selected range' })}
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-sm border-border">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('dashboard.processing')}</CardTitle>
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary animate-pulse">
                  <Activity className="h-4 w-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-black text-foreground">{stats.processing}</div>
                <div className="flex items-center text-xs text-muted-foreground mt-2 font-medium">
                  {t('dashboard.processingDesc', { defaultValue: 'Active dispatches right now' })}
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-sm border-border relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-rose-500/20 to-transparent rounded-bl-full pointer-events-none" />
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('dashboard.failed')}</CardTitle>
                <div className="w-8 h-8 rounded-full bg-rose-500/10 flex items-center justify-center text-rose-500">
                  <TrendingDown className="h-4 w-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-black text-foreground">{stats.notSended}</div>
                <div className="flex items-center text-xs text-rose-600 mt-2 font-medium">
                  {t('dashboard.failedDesc', { defaultValue: 'Failed queue items need review' })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CRM Intelligence Funnel Block */}
          {crmFunnel && (
            <Card className="col-span-full shadow-sm border-border overflow-hidden">
              <div className="bg-gradient-to-r from-muted/50 to-muted/10 px-6 py-4 border-b border-border flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" /> CRM Intelligence Funnel
                  </CardTitle>
                  <CardDescription className="font-medium mt-1">Lead pool health and validation state overview</CardDescription>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/crm/contacts')}
                  className="text-[11px] font-black uppercase tracking-widest text-primary hover:underline"
                >
                  Open CRM
                </button>
              </div>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="border border-border rounded-xl p-4 bg-background shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Total Leads</div>
                      <Users className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="text-3xl font-black">{crmFunnel.total || 0}</div>
                    <div className="text-xs text-muted-foreground mt-2 font-medium bg-muted/50 p-2 rounded-lg">
                      <span className="text-primary">{crmFunnel.contacted || 0}</span> contacted <br/>
                      <span>{crmFunnel.uncontacted || 0}</span> waiting
                    </div>
                  </div>
                  
                  <div className="border border-border rounded-xl p-4 bg-background shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Verification</div>
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    </div>
                    <div className="flex gap-2 text-sm font-semibold flex-col mt-2">
                      {Object.keys(crmFunnel.verificationDistribution || {}).length === 0 ? (
                        <div className="text-xs text-muted-foreground">No verification data</div>
                      ) : Object.entries(crmFunnel.verificationDistribution).map(([k, v]) => {
                        const vLabel = { VERIFIED: 'Verified', RISKY: 'Risky', INVALID: 'Invalid', VERIFYING: 'Verifying', UNKNOWN: 'Unknown' }[k] || k;
                        const vColor = k === 'VERIFIED' ? 'text-emerald-500' : k === 'RISKY' ? 'text-amber-500' : k === 'INVALID' ? 'text-rose-500' : 'text-muted-foreground';
                        return (
                          <div key={k} className="flex justify-between items-center text-xs px-2 py-1 rounded bg-muted/30">
                            <span className={`flex items-center gap-1.5 ${vColor}`}>
                              <span className={`w-2 h-2 rounded-full ${k === 'VERIFIED' ? 'bg-emerald-500' : k === 'RISKY' ? 'bg-amber-500' : k === 'INVALID' ? 'bg-rose-500' : 'bg-muted-foreground'}`} />
                              {vLabel}
                            </span>
                            <span className="font-bold text-foreground">{v}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="border border-border rounded-xl p-4 bg-background shadow-sm hover:shadow-md transition-shadow col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Pipeline Stages</div>
                      <Layers className="w-4 h-4 text-blue-500" />
                    </div>
                    <div className="flex gap-2 text-xs flex-wrap mt-3">
                      {Object.keys(crmFunnel.stageDistribution || {}).length === 0 ? (
                        <div className="text-muted-foreground">No pipeline stages recorded yet.</div>
                      ) : Object.entries(crmFunnel.stageDistribution).map(([k, v]) => {
                        const sLabel = { LEAD: 'Lead', CONTACTED: 'Contacted', REPLIED: 'Replied', MEETING_BOOKED: 'Meeting', CLOSED_WON: 'Won', CLOSED_LOST: 'Lost', UNQUALIFIED: 'Unqualified' }[k] || k;
                        return (
                          <div key={k} className="bg-muted border border-border px-3 py-1.5 rounded-lg flex items-center gap-2">
                            <span className="font-medium text-muted-foreground">{sLabel}</span>
                            <span className="font-bold text-foreground bg-background px-1.5 rounded">{v}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">
            {/* Sequence Engine Pulse Overview */}
            <Card className="col-span-1 lg:col-span-3 shadow-sm border-border">
              <CardHeader className="pb-4 border-b border-border/50">
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MousePointerClick className="h-5 w-5 text-indigo-500" /> Active Sequences
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/sequences')} className="h-8 text-xs font-semibold text-primary hover:bg-primary/10">
                    Manage All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {loading ? <Skeleton className="h-48 w-full rounded-xl" /> :
                  (!sequenceData || !sequenceData.aggregateStats ? <div className="flex flex-col items-center justify-center py-10 text-muted-foreground bg-muted/20 rounded-xl border border-dashed border-border"><MousePointerClick className="w-8 h-8 opacity-20 mb-3" /><span>No active sequence data.</span></div> : (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-indigo-500/5 border border-indigo-500/10 rounded-xl p-4">
                          <div className="text-xs text-indigo-600/80 uppercase font-bold tracking-wider mb-1">Total Enrolled</div>
                          <div className="text-3xl font-black text-indigo-700 dark:text-indigo-400">{sequenceData.aggregateStats?.totalAssigned || 0}</div>
                        </div>
                        <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-xl p-4">
                          <div className="text-xs text-emerald-600/80 uppercase font-bold tracking-wider mb-1">Actively Sending</div>
                          <div className="text-3xl font-black text-emerald-700 dark:text-emerald-400">{sequenceData.aggregateStats?.totalActive || 0}</div>
                        </div>
                      </div>

                      <div className="space-y-3 pt-2">
                        <div className="flex justify-between items-center p-3 rounded-lg border border-border bg-background shadow-sm hover:shadow-md transition-shadow">
                          <span className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500" /> Replied (Yield)
                          </span>
                          <span className="font-bold bg-blue-500/10 text-blue-600 px-3 py-1 rounded-full text-sm">
                            {sequenceData.aggregateStats?.totalReplied || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg border border-border bg-background shadow-sm hover:shadow-md transition-shadow">
                          <span className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" /> Successfully Finished
                          </span>
                          <span className="font-bold bg-emerald-500/10 text-emerald-600 px-3 py-1 rounded-full text-sm">
                            {sequenceData.aggregateStats?.totalFinished || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-3 rounded-lg border border-border bg-background shadow-sm hover:shadow-md transition-shadow">
                          <span className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-rose-500" /> Bounced / Failed
                          </span>
                          <span className="font-bold bg-rose-500/10 text-rose-600 px-3 py-1 rounded-full text-sm">
                            {sequenceData.aggregateStats?.totalBounced || 0}
                          </span>
                        </div>
                      </div>

                      {highlightedSequences.length > 0 && (
                        <div className="space-y-3 border-t border-border/50 pt-4">
                          <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">
                            Sequence Watchlist
                          </div>
                          {highlightedSequences.map((sequence) => (
                            <button
                              key={sequence._id}
                              type="button"
                              onClick={() => navigate(`/sequences/${sequence._id}`)}
                              className="w-full rounded-xl border border-border bg-background px-4 py-3 text-left hover:border-primary/40 hover:bg-primary/5 transition-all"
                            >
                              <div className="flex items-center justify-between gap-3">
                                <span className="font-bold text-foreground">{sequence.name}</span>
                                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                                  {sequence.status}
                                </span>
                              </div>
                              <div className="mt-2 text-xs text-muted-foreground">
                                {sequence.stats?.replied || 0} replies · {sequence.stats?.bounced || 0} bounced · {sequence.stats?.active || 0} active
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </CardContent>
            </Card>

            {/* Main Overview Chart */}
            <Card className="col-span-1 lg:col-span-4 shadow-sm border-border">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-gray-500" /> Daily Volume Overview</CardTitle>
                <CardDescription>Email sending operations over the selected period</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[380px] w-full">
                  {loading ? (
                    <Skeleton className="h-full w-full rounded-xl" />
                  ) : barData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={barData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} dy={10} />
                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `${value}`} />
                        <ReTooltip
                          cursor={{ fill: 'hsl(var(--muted)/0.5)', radius: 4 }}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--background))',
                            borderColor: 'hsl(var(--border))',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                            fontWeight: '600'
                          }}
                        />
                        <Bar dataKey="total" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} maxBarSize={40} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground bg-muted/10 rounded-xl border border-dashed border-border p-6 text-center">
                      <BarChart3 className="w-10 h-10 opacity-20 mb-4" />
                      <p className="font-medium">No volume data in this interval.</p>
                      <p className="text-xs opacity-70 mt-1">Adjust the date range to see historical volume.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">
            {/* Recent Activity Table */}
            <Card className="col-span-1 lg:col-span-4 shadow-sm border-border">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-lg">{t('dashboard.recentActivity')}</CardTitle>
                <CardDescription>
                  Live log of recently attempted dispatches
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {recentEmails.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground font-medium">No recent activity found.</div>
                ) : (
                  <Table>
                    <TableHeader className="bg-muted/30">
                        <TableRow>
                          <TableHead className="w-[60px]"></TableHead>
                          <TableHead>Recipient</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Status Reason</TableHead>
                          <TableHead className="text-right whitespace-nowrap">Time</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentEmails.map((email) => {
                        const status = email.status || 'sent';
                        return (
                          <TableRow key={email._id} className="hover:bg-muted/30 transition-colors">
                            <TableCell className="py-3">
                              <Avatar className="h-8 w-8 border border-border">
                                <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                                  {(email.firstName || email.to || "?").substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                            </TableCell>
                            <TableCell className="py-3">
                              <div className="font-semibold text-sm text-foreground truncate max-w-[150px] md:max-w-[200px]" title={email.to || email.email}>
                                {email.to || email.email}
                              </div>
                              <div className="text-[11px] text-muted-foreground truncate max-w-[150px] md:max-w-[200px] mt-0.5" title={email.subject}>
                                {email.subject || "No Subject"}
                              </div>
                            </TableCell>
                            <TableCell className="py-3">
                              <div className="text-xs font-bold text-foreground">
                                {email.sourceName || 'Workspace Mail'}
                              </div>
                              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                {email.sourceType || 'OUTBOUND'}
                              </div>
                            </TableCell>
                            <TableCell className="py-3">
                              <Badge variant={
                                status === 'sent' ? 'success' :
                                  status === 'failed' ? 'destructive' : 'outline'
                              } className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider">
                                {t(`status.${status}`) || status}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-3">
                              <div className="text-xs text-muted-foreground max-w-[180px]">
                                {email.statusReason || 'Dispatch completed without extra notes.'}
                              </div>
                            </TableCell>
                            <TableCell className="text-right py-3 text-xs font-medium text-muted-foreground whitespace-nowrap">
                              {dayjs(email.sentAt).format('D MMM')} <br/>
                              <span className="text-[10px]">{dayjs(email.sentAt).format('HH:mm')}</span>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Country Distribution Chart */}
            <Card className="col-span-1 lg:col-span-3 shadow-sm border-border">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-lg">{t('dashboard.distributionByCountry')}</CardTitle>
                <CardDescription>Geographic outreach map</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="h-[300px] w-full flex items-center justify-center">
                  {!countryData || countryData.length === 0 ? (
                    <div className="text-muted-foreground font-medium flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full border-4 border-dashed border-border flex items-center justify-center mb-3">
                        <span className="text-xl">🌍</span>
                      </div>
                      No geography data
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={countryData}
                          cx="50%"
                          cy="50%"
                          startAngle={90}
                          endAngle={-270}
                          innerRadius={65}
                          outerRadius={95}
                          paddingAngle={3}
                          dataKey="value"
                          stroke="none"
                        >
                          {countryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <ReTooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--background))',
                            borderColor: 'hsl(var(--border))',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            fontWeight: '600',
                            padding: '8px 12px'
                          }}
                          itemStyle={{ fontSize: '13px' }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          iconType="circle"
                          wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                          formatter={(value) => <span className="text-foreground font-medium">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="col-span-1 lg:col-span-3 shadow-sm border-border">
              <CardHeader className="border-b border-border/50 pb-4">
                <CardTitle className="text-lg">Campaign Readiness</CardTitle>
                <CardDescription>Most recent campaigns that are ready or need operator focus</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-3">
                {highlightedCampaigns.length === 0 ? (
                  <div className="text-muted-foreground font-medium">No campaign signals available.</div>
                ) : (
                  highlightedCampaigns.map((campaign) => (
                    <button
                      key={campaign._id}
                      type="button"
                      onClick={() => navigate(`/campaigns/${campaign._id}`)}
                      className="w-full rounded-xl border border-border bg-background px-4 py-3 text-left hover:border-primary/40 hover:bg-primary/5 transition-all"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-bold text-foreground">{campaign.name}</span>
                        <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{campaign.status}</span>
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        {campaign.stats?.sent || 0} sent · {campaign.stats?.replied || 0} replied · {campaign.stats?.failed || 0} failed
                      </div>
                    </button>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="executive">
          <UnifiedInsightsDashboard onNavigate={(route) => navigate(`/${route}`)} />
        </TabsContent>

        <TabsContent value="revenue">
          <RevenueDashboard />
        </TabsContent>
      </Tabs>
    </div>
  </>
  );
}
